<script lang="ts">
  /**
   * One editable text field of a briefing.
   *
   * Plain text only, deliberately: the contract has no rich-text field, and a
   * briefing is team-wide, so one person's edit renders in everyone else's
   * browser. Storing markup there would be storing a cross-site scripting
   * payload. We read `textContent` and nothing else, so a rich paste that slips
   * past `plaintext-only` still lands as text.
   */
  let { value, editing = false, placeholder = '', ariaLabel, onchange }: {
    value: string;
    editing?: boolean;
    placeholder?: string;
    ariaLabel: string;
    onchange?: (next: string) => void;
  } = $props();

  let el = $state<HTMLSpanElement>();

  // Firefox has no `plaintext-only`, and an unsupported value leaves the element
  // uneditable rather than falling back. Detect once and use `true` there; the
  // paste handler below is what keeps the content plain either way.
  const mode = (() => {
    if (typeof document === 'undefined') return 'true';
    const probe = document.createElement('div');
    probe.contentEditable = 'plaintext-only';
    return probe.contentEditable === 'plaintext-only' ? 'plaintext-only' : 'true';
  })();

  // This effect is the field's only text writer: a `{value}` child would rewrite
  // the focused text node on every keystroke's round trip and reset the caret.
  $effect(() => {
    if (!el) return;
    if (el.textContent !== value && document.activeElement !== el) el.textContent = value;
  });
  function onblur() {
    if (el && el.textContent !== value) el.textContent = value;
  }

  function onpaste(event: ClipboardEvent) {
    const text = event.clipboardData?.getData('text/plain') ?? '';
    event.preventDefault();
    document.execCommand('insertText', false, text.replace(/\s*\n\s*/g, ' '));
  }
</script>

<span
  bind:this={el}
  class="brf-ed"
  class:editing
  contenteditable={editing ? mode : undefined}
  role={editing ? 'textbox' : undefined}
  aria-label={editing ? ariaLabel : undefined}
  aria-multiline={editing ? 'false' : undefined}
  data-placeholder={placeholder}
  oninput={() => onchange?.(el?.textContent ?? '')}
  {onpaste}
  {onblur}
  onkeydown={(event) => { if (event.key === 'Enter') event.preventDefault(); }}
></span>
